import { API_RESPONSE_STATUS_CODE } from '@app/constants/common';
import { doDelete, doGet, doPatch, doPost } from '@app/api';
import { IDoctor } from '@app/interfaces/doctor.interface';
import { fetchClinics } from '@app/utils/clinic';
import type { IClinic } from '@app/interfaces/clinic.interface';

export const fetchDoctorsByClinic = async (clinicId: number, feedbackSiteId?: number) => {
  try {
    const result = await doGet(`/api/doctor`, {
      params: {
        clinicId,
        feedbackSiteId,
      },
    });
    if (result.status === API_RESPONSE_STATUS_CODE.SUCCESS) {
      return result.data;
    }
  } catch (e) {
    console.log('e', e);
  }
};

export const fetchDoctors = async () => {
  try {
    const clinicsResponse: { clinics: IClinic[] } | undefined = await fetchClinics();
    const clinics = clinicsResponse?.clinics || [];

    const doctorsByClinic = await Promise.all(
      clinics.map(async (clinic) => {
        const doctors: IDoctor[] = (await fetchDoctorsByClinic(clinic.id)) || [];

        return doctors.map((doctor) => ({
          ...doctor,
          clinic: doctor.clinic || clinic,
          clinics: doctor.clinics?.length ? doctor.clinics : [clinic],
        }));
      })
    );

    const doctorsMap = doctorsByClinic.flat().reduce((result, doctor) => {
      const currentDoctor = result.get(doctor.id);

      if (!currentDoctor) {
        result.set(doctor.id, doctor);
        return result;
      }

      const clinics = [...(currentDoctor.clinics || []), ...(doctor.clinics || [])].reduce(
        (clinicsMap, clinic) => clinicsMap.set(clinic.id, clinic),
        new Map<number, IClinic>()
      );

      result.set(doctor.id, {
        ...currentDoctor,
        clinics: Array.from(clinics.values()),
      });

      return result;
    }, new Map<number, IDoctor>());

    return Array.from(doctorsMap.values());
  } catch (e) {
    console.log('e', e);
  }
};

export const addDoctor = async (clinicId: number, doctor: Partial<IDoctor>) => {
  try {
    const result = await doPost('/api/doctor', {
      clinicId,
      doctor,
    });
    if (result.status === API_RESPONSE_STATUS_CODE.CREATED) {
      return result.data;
    }
  } catch (e) {
    console.log('e', e);
  }
};

export const updateDoctor = async (doctor: Partial<IDoctor>) => {
  try {
    const result = await doPatch(`/api/doctor/${doctor.id}`, {
      doctor,
    });
    if (result.status === API_RESPONSE_STATUS_CODE.SUCCESS) {
      return result.data;
    }
  } catch (e) {
    console.log('e', e);
  }
};

export const deleteDoctor = async (doctor: Partial<IDoctor>, feedbackSiteId?: number) => {
  try {
    const result = await doDelete(`/api/doctor/${doctor.id}`, {
      params: { feedbackSiteId },
    });
    if (result.status === API_RESPONSE_STATUS_CODE.SUCCESS) {
      return result.data;
    }
  } catch (e) {
    console.log('e', e);
  }
};
